import React, { useState } from "react";

import ROUTES from "manager/config/routes";
import { useToggleVeroGo } from "manager/hooks/api/portfolioQueries";

import PropertyRow from "./PropertyRow";
import { GreenSwitch, SettingsLink } from "./shared";

type PortfolioSectionProps = {
  portfolio: any;
  properties: any[];
  isLocked: boolean;
  forceExpand?: boolean;
  onAfterToggle?: (newValue: boolean) => void;
  onStartMutation?: (itemType?: string, itemId?: number) => void;
  flash?: boolean;
  flashPropertyId?: number;
};

const PortfolioSection = ({
  portfolio,
  properties,
  isLocked,
  forceExpand,
  onAfterToggle,
  onStartMutation,
  flash,
  flashPropertyId,
}: PortfolioSectionProps) => {
  const isActive = !!portfolio.veroGoActive;
  const [collapsed, setCollapsed] = useState(true);

  React.useEffect(() => {
    if (forceExpand) setCollapsed(false);
  }, [forceExpand]);

  const { toggleVeroGo, isToggleVeroGoLoading } = useToggleVeroGo({
    portfolioId: portfolio.id,
  });

  return (
    <div
      data-vero-go-item={`portfolio-${portfolio.id}`}
      style={{
        background: "#fff",
        border: "1px solid #e8e8e8",
        borderLeft: "3px solid #3b82f6",
        borderRadius: 8,
        marginBottom: 8,
        overflow: "hidden",
      }}
      className={flash ? "vero-go-flash" : undefined}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 20px",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setCollapsed(!collapsed)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setCollapsed(!collapsed);
        }}
        role="button"
        tabIndex={0}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                display: "inline-block",
                transition: "transform 200ms",
                transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
                color: "#999",
                fontSize: 14,
              }}
            >
              &#8964;
            </span>
            <SettingsLink
              to={ROUTES.portfolioSettings.replace(":id", String(portfolio.id))}
              style={{ fontWeight: 700 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {portfolio.name}
            </SettingsLink>
            <span style={{ color: "#999", fontSize: 13 }}>
              ({properties.length}{" "}
              {properties.length === 1 ? "property" : "properties"})
            </span>
          </div>
        </div>
        <div
          style={{ display: "flex", alignItems: "center", gap: 12 }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          <GreenSwitch
            checked={isActive}
            loading={isToggleVeroGoLoading}
            onChange={(checked) => {
              onStartMutation?.("portfolio", portfolio.id);
              toggleVeroGo(checked, {
                onSettled: () => onAfterToggle?.(checked),
              });
            }}
            disabled={isToggleVeroGoLoading || isLocked}
          />
        </div>
      </div>
      {!collapsed && (
        <div style={{ borderTop: "1px solid #f0f0f0" }}>
          {properties.length > 0 ? (
            properties.map((property) => (
              <PropertyRow
                key={property.id}
                property={property}
                portfolioName={portfolio.name}
                portfolioActive={isActive}
                isLocked={isLocked}
                onAfterToggle={onAfterToggle}
                onStartMutation={onStartMutation}
                flash={flashPropertyId === property.id}
              />
            ))
          ) : (
            <div
              style={{
                padding: "12px 20px 12px 48px",
                color: "#999",
                fontStyle: "italic",
              }}
            >
              No properties in this portfolio
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PortfolioSection;
